import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import AdminNav from '../components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('banner');
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [settings, setSettings] = useState({
    banner_mode: 'launch',
    launch_banner_text: '',
    promo_banner_text: '',
    banner_background_color: '',
    banner_text_color: '',
    enable_close_button: true
  });
  const [seoSettings, setSeoSettings] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image_url: '',
    canonical_url: '',
    robots_meta: 'index, follow',
    enable_sitemap: true,
    enable_robots: true
  });
  const [analyticsSettings, setAnalyticsSettings] = useState({
    google_analytics_id: '',
    google_tag_manager_id: '',
    facebook_pixel_id: '',
    linkedin_insight_id: '',
    microsoft_ads_id: '',
    tiktok_pixel_id: ''
  });
  const [contentSettings, setContentSettings] = useState({
    homepage_title: '',
    homepage_subtitle: '',
    homepage_cta_text: '',
    header_promo_text: '',
    header_promo_enabled: true,
    footer_promo_text: '',
    footer_copyright: '',
    pricing_title: '',
    pricing_subtitle: '',
    meta_title: '',
    meta_description: ''
  });
  const [launchOfferSettings, setLaunchOfferSettings] = useState({
    launch_offer_enabled: true,
    launch_offer_text: '🎉 Enjoy 1 month of Premium FREE with all new sign-ups. Offer ends Jan 15, 2026.',
    launch_offer_expiry: '2026-01-15T23:59:59Z',
    launch_offer_type: 'one_month_free'
  });
  const [seasonalSettings, setSeasonalSettings] = useState({
    snow_enabled: true,
    holiday_popup_enabled: true
  });
  const [toolVisibility, setToolVisibility] = useState({
    merge: true,
    split: true,
    compress: true,
    convert_to_jpg: true,
    pdf_to_word: true,
    word_to_pdf: true,
    xfa_to_pdf: true,
    image_to_pdf: true,
    pdf_to_ppt: true
  });

  useEffect(() => {
    checkAuth();
    fetchSettings();
    fetchSEOSettings();
    fetchAnalyticsSettings();
    fetchContentSettings();
    fetchLaunchOfferSettings();
    fetchSeasonalSettings();
    fetchToolVisibility();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, fetchUsers]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login as admin');
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${API}/user/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.role !== 'admin') {
        toast.error('Admin access required');
        navigate('/');
      }
    } catch (error) {
      toast.error('Authentication failed');
      navigate('/');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/banner`);
      setSettings({
        banner_mode: response.data.banner_mode || 'launch',
        launch_banner_text: response.data.launch_banner_text || '',
        promo_banner_text: response.data.promo_banner_text || '',
        banner_background_color: response.data.banner_background_color || '',
        banner_text_color: response.data.banner_text_color || '',
        enable_close_button: response.data.enable_close_button !== undefined ? response.data.enable_close_button : true
      });
    } catch (error) {
      console.error('Failed to fetch banner settings:', error);
      toast.error('Failed to load banner settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSEOSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/seo`);
      setSeoSettings({
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || '',
        og_title: response.data.og_title || '',
        og_description: response.data.og_description || '',
        og_image_url: response.data.og_image_url || '',
        canonical_url: response.data.canonical_url || '',
        robots_meta: response.data.robots_meta || 'index, follow',
        enable_sitemap: response.data.enable_sitemap !== undefined ? response.data.enable_sitemap : true,
        enable_robots: response.data.enable_robots !== undefined ? response.data.enable_robots : true
      });
    } catch (error) {
      console.error('Failed to fetch SEO settings:', error);
      toast.error('Failed to load SEO settings');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/banner/admin`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      localStorage.removeItem('bannerDismissed_launch');
      localStorage.removeItem('bannerDismissed_promo');
      
      toast.success('Banner settings updated successfully! 🎉');
    } catch (error) {
      console.error('Failed to save banner settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save banner settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSEO = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/seo/admin`,
        seoSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('SEO settings updated successfully! 🎉');
    } catch (error) {
      console.error('Failed to save SEO settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchAnalyticsSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/analytics`);
      setAnalyticsSettings({
        google_analytics_id: response.data.google_analytics_id || '',
        google_tag_manager_id: response.data.google_tag_manager_id || '',
        facebook_pixel_id: response.data.facebook_pixel_id || '',
        linkedin_insight_id: response.data.linkedin_insight_id || '',
        microsoft_ads_id: response.data.microsoft_ads_id || '',
        tiktok_pixel_id: response.data.tiktok_pixel_id || ''
      });
    } catch (error) {
      console.error('Failed to fetch analytics settings:', error);
      toast.error('Failed to load analytics settings');
    }
  };

  const handleSaveAnalytics = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/analytics/admin`,
        analyticsSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Analytics settings updated successfully! 🎉');
    } catch (error) {
      console.error('Failed to save analytics settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save analytics settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchContentSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/content`);
      setContentSettings({
        homepage_title: response.data.homepage_title || '',
        homepage_subtitle: response.data.homepage_subtitle || '',
        homepage_cta_text: response.data.homepage_cta_text || '',
        header_promo_text: response.data.header_promo_text || '',
        header_promo_enabled: response.data.header_promo_enabled !== undefined ? response.data.header_promo_enabled : true,
        footer_promo_text: response.data.footer_promo_text || '',
        footer_copyright: response.data.footer_copyright || '',
        pricing_title: response.data.pricing_title || '',
        pricing_subtitle: response.data.pricing_subtitle || '',
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || ''
      });
    } catch (error) {
      console.error('Failed to fetch content settings:', error);
      toast.error('Failed to load content settings');
    }
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/content/admin`,
        contentSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Content settings updated successfully! 🎉');
    } catch (error) {
      console.error('Failed to save content settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save content settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchLaunchOfferSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/launch-offer`);
      setLaunchOfferSettings({
        launch_offer_enabled: response.data.launch_offer_enabled !== undefined ? response.data.launch_offer_enabled : true,
        launch_offer_text: response.data.launch_offer_text || '🎉 Enjoy 1 month of Premium FREE with all new sign-ups. Offer ends Jan 15, 2026.',
        launch_offer_expiry: response.data.launch_offer_expiry || '2026-01-15T23:59:59Z',
        launch_offer_type: response.data.launch_offer_type || 'one_month_free'
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch launch offer settings:', error);
      setLoading(false);
    }
  };

  const handleSaveLaunchOffer = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/launch-offer/admin`,
        launchOfferSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Launch offer settings updated successfully! 🎉');
      // Clear banner dismissal to show updated offer
      localStorage.removeItem('bannerDismissed_launch');
    } catch (error) {
      console.error('Failed to save launch offer settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save launch offer settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchSeasonalSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/seasonal`);
      setSeasonalSettings({
        snow_enabled: response.data.snow_enabled !== undefined ? response.data.snow_enabled : true,
        holiday_popup_enabled: response.data.holiday_popup_enabled !== undefined ? response.data.holiday_popup_enabled : true,
        auto_disabled: response.data.auto_disabled || false,
        cutoff_date: response.data.cutoff_date || '2026-01-02T00:00:00'
      });
    } catch (error) {
      console.error('Failed to fetch seasonal settings:', error);
    }
  };

  const handleSaveSeasonalSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/settings/seasonal`,
        {
          snow_enabled: seasonalSettings.snow_enabled,
          holiday_popup_enabled: seasonalSettings.holiday_popup_enabled
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Seasonal settings updated successfully! ❄️');
    } catch (error) {
      console.error('Failed to save seasonal settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save seasonal settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchToolVisibility = async () => {
    try {
      const response = await axios.get(`${API}/settings/tools`);
      setToolVisibility({
        merge: response.data.merge !== undefined ? response.data.merge : true,
        split: response.data.split !== undefined ? response.data.split : true,
        compress: response.data.compress !== undefined ? response.data.compress : true,
        convert_to_jpg: response.data.convert_to_jpg !== undefined ? response.data.convert_to_jpg : true,
        pdf_to_word: response.data.pdf_to_word !== undefined ? response.data.pdf_to_word : true,
        word_to_pdf: response.data.word_to_pdf !== undefined ? response.data.word_to_pdf : true,
        xfa_to_pdf: response.data.xfa_to_pdf !== undefined ? response.data.xfa_to_pdf : true,
        image_to_pdf: response.data.image_to_pdf !== undefined ? response.data.image_to_pdf : true,
        pdf_to_ppt: response.data.pdf_to_ppt !== undefined ? response.data.pdf_to_ppt : true
      });
    } catch (error) {
      console.error('Failed to fetch tool visibility settings:', error);
    }
  };

  const handleSaveToolVisibility = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/settings/tools`,
        toolVisibility,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Tool visibility settings updated successfully! 🛠️');
    } catch (error) {
      console.error('Failed to save tool visibility settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tool visibility settings');
    } finally {
      setSaving(false);
    }
  };


  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/users?skip=0&limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  }, []);

  const handleUpdateSubscription = async (userEmail, newStatus, expiryDate = null) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const subscriptionData = {
        subscription_status: newStatus,
        subscription_expiry: expiryDate
      };

      await axios.put(
        `${API}/admin/users/${encodeURIComponent(userEmail)}/subscription`,
        subscriptionData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Subscription updated successfully!');
      setShowUserModal(false);
      setSelectedUser(null);
      await fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Failed to update subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userEmail) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API}/admin/users/${encodeURIComponent(userEmail)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('User deleted successfully!');
      setShowUserModal(false);
      setSelectedUser(null);
      await fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    if (users.length === 0) {
      toast.error('No users to export');
      return;
    }

    // Define CSV headers
    const headers = ['Email', 'Subscription Status', 'Role', 'Registered Date', 'Subscription Expiry', 'Needs Password Setup'];
    
    // Convert users to CSV rows
    const rows = users.map(user => {
      const registeredDate = user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A';
      const expiryDate = user.subscription_expiry_date ? new Date(user.subscription_expiry_date).toLocaleDateString() : 'N/A';
      
      return [
        user.email || '',
        user.subscription_status || 'free',
        user.role || 'user',
        registeredDate,
        expiryDate,
        user.needs_password_setup ? 'Yes' : 'No'
      ];
    });

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `smartpdf_users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${users.length} users to CSV`);
  };



  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <p style={{ fontSize: '1.2rem', color: '#4a5568' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'transparent',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '600',
                color: '#4a5568',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.color = '#2563eb';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#4a5568';
              }}
            >
              ← Back to Home
            </button>
          </div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1a202c',
            margin: '0'
          }}>
            ⚙️ Site Settings
          </h1>
          <p style={{ color: '#718096', marginTop: '8px', fontSize: '1.05rem' }}>
            Manage your site's promotional banners, SEO, and appearance
          </p>
        </div>

        {/* Navigation */}
        <AdminNav />

        {/* Tab Navigation */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '0',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e2e8f0'
          }}>
            <button
              onClick={() => setActiveTab('banner')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'banner' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'banner' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'banner' ? 'none' : '2px solid transparent'
              }}
            >
              🎨 Banner Settings
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'seo' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'seo' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'seo' ? 'none' : '2px solid transparent'
              }}
            >
              🔍 SEO Settings
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'analytics' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'analytics' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'analytics' ? 'none' : '2px solid transparent'
              }}
            >
              📊 Analytics & Tracking
            </button>
            <button
              onClick={() => setActiveTab('content')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'content' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'content' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'content' ? 'none' : '2px solid transparent'
              }}
            >
              ✏️ Basic Content
            </button>
            <button
              onClick={() => setActiveTab('launch-offer')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'launch-offer' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'launch-offer' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'launch-offer' ? 'none' : '2px solid transparent'
              }}
            >
              🚀 Launch Offer
            </button>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'users' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'users' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'users' ? 'none' : '2px solid transparent'
              }}
            >
              👥 User Management
            </button>
            <button
              onClick={() => setActiveTab('seasonal')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'seasonal' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'seasonal' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'seasonal' ? 'none' : '2px solid transparent'
              }}
            >
              ❄️ Seasonal
            </button>
            <button
              onClick={() => setActiveTab('tools')}
              style={{
                flex: 1,
                padding: '16px 24px',
                border: 'none',
                background: activeTab === 'tools' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                color: activeTab === 'tools' ? 'white' : '#4a5568',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderBottom: activeTab === 'tools' ? 'none' : '2px solid transparent'
              }}
            >
              🛠️ Tools
            </button>
          </div>
        </div>

        {/* Banner Settings Tab */}
        {activeTab === 'banner' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              🎨 Homepage Banner Settings
            </h2>

            {/* Banner Mode Selector */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Select Banner Type to Display
              </label>
              <select
                value={settings.banner_mode}
                onChange={(e) => setSettings({ ...settings, banner_mode: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  background: 'white'
                }}
              >
                <option value="launch">🚀 Launch Offer Banner Only</option>
                <option value="promo">🎟️ Promo Code Banner Only</option>
                <option value="both">🎉 Show Both Banners (Launch on Top)</option>
                <option value="none">🚫 Hide All Banners</option>
              </select>
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Changes take effect immediately without requiring deployment
              </p>
            </div>

            {/* Launch Banner Text */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Launch Banner Text (Optional)
              </label>
              <textarea
                value={settings.launch_banner_text}
                onChange={(e) => setSettings({ ...settings, launch_banner_text: e.target.value })}
                placeholder="Leave empty to use default text"
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Promo Banner Text */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Promo Code Banner Text (Optional)
              </label>
              <textarea
                value={settings.promo_banner_text}
                onChange={(e) => setSettings({ ...settings, promo_banner_text: e.target.value })}
                placeholder="Leave empty to use default text"
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Banner Background Color */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Banner Background Color (Optional)
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={settings.banner_background_color || '#FFF8D1'}
                  onChange={(e) => setSettings({ ...settings, banner_background_color: e.target.value })}
                  style={{
                    width: '60px',
                    height: '44px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={settings.banner_background_color}
                  onChange={(e) => setSettings({ ...settings, banner_background_color: e.target.value })}
                  placeholder="#FFF8D1 (default)"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '1rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                {settings.banner_background_color && (
                  <button
                    onClick={() => setSettings({ ...settings, banner_background_color: '' })}
                    style={{
                      padding: '12px 20px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Banner Text Color */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Banner Text Color (Optional)
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={settings.banner_text_color || '#1A1A1A'}
                  onChange={(e) => setSettings({ ...settings, banner_text_color: e.target.value })}
                  style={{
                    width: '60px',
                    height: '44px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={settings.banner_text_color}
                  onChange={(e) => setSettings({ ...settings, banner_text_color: e.target.value })}
                  placeholder="#1A1A1A (default)"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '1rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                {settings.banner_text_color && (
                  <button
                    onClick={() => setSettings({ ...settings, banner_text_color: '' })}
                    style={{
                      padding: '12px 20px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Enable Close Button */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={settings.enable_close_button}
                  onChange={(e) => setSettings({ ...settings, enable_close_button: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#2d3748'
                }}>
                  Enable Close Button on Banners
                </span>
              </label>
            </div>

            {/* Save Button */}
            <div style={{
              borderTop: '2px solid #e2e8f0',
              paddingTop: '20px',
              marginTop: '30px',
              display: 'flex',
              gap: '12px',
              flexDirection: 'column'
            }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: saving ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  width: '100%'
                }}
              >
                {saving ? '💾 Saving...' : '💾 Save Banner Settings'}
              </button>
              
              <button
                onClick={() => {
                  localStorage.removeItem('bannerDismissed_launch');
                  localStorage.removeItem('bannerDismissed_promo');
                  window.open('/', '_blank');
                }}
                style={{
                  background: 'white',
                  color: '#2563eb',
                  border: '2px solid #2563eb',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  width: '100%'
                }}
              >
                👁️ Preview Homepage (New Tab)
              </button>
            </div>

            {/* Info Box */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: 'linear-gradient(135deg, #ebf8ff 0%, #e0f2fe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: '#1e40af',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>ℹ️</span> Important Information
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: '#1e40af',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}>
                <li>Banner changes are applied instantly without requiring a new deployment</li>
                <li><strong>After saving, use "Preview Homepage" button or refresh your homepage to see changes</strong></li>
                <li>The system automatically clears any previously dismissed banners when you save</li>
                <li>If settings fail to load, the default "Launch Offer" banner will be displayed</li>
                <li>Custom text and colors are optional - leave them empty to use defaults</li>
              </ul>
            </div>
          </div>
        )}

        {/* SEO Settings Tab */}
        {activeTab === 'seo' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              🔍 SEO Settings
            </h2>

            {/* Meta Title */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Meta Title
              </label>
              <input
                type="text"
                value={seoSettings.meta_title}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_title: e.target.value })}
                placeholder="SmartPDF Tools - Professional PDF Processing Online"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Recommended length: 50-60 characters
              </p>
            </div>

            {/* Meta Description */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Meta Description
              </label>
              <textarea
                value={seoSettings.meta_description}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_description: e.target.value })}
                placeholder="Merge, split, compress, and convert your PDFs with ease. Fast, secure, and production-ready PDF tools."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Recommended length: 150-160 characters
              </p>
            </div>

            {/* Meta Keywords */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Meta Keywords (comma-separated)
              </label>
              <input
                type="text"
                value={seoSettings.meta_keywords}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_keywords: e.target.value })}
                placeholder="PDF tools, merge PDF, split PDF, compress PDF, convert PDF"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
            </div>

            {/* OG Title */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Open Graph Title
              </label>
              <input
                type="text"
                value={seoSettings.og_title}
                onChange={(e) => setSeoSettings({ ...seoSettings, og_title: e.target.value })}
                placeholder="SmartPDF Tools - Professional PDF Processing"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                For social media sharing (Facebook, LinkedIn, etc.)
              </p>
            </div>

            {/* OG Description */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Open Graph Description
              </label>
              <textarea
                value={seoSettings.og_description}
                onChange={(e) => setSeoSettings({ ...seoSettings, og_description: e.target.value })}
                placeholder="Professional PDF tools for merging, splitting, compressing, and converting PDFs online."
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* OG Image URL */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Open Graph Image URL
              </label>
              <input
                type="url"
                value={seoSettings.og_image_url}
                onChange={(e) => setSeoSettings({ ...seoSettings, og_image_url: e.target.value })}
                placeholder="https://smrtpdftools.com/og-image.jpg"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Recommended size: 1200×630 pixels
              </p>
            </div>

            {/* Canonical URL */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Canonical URL
              </label>
              <input
                type="url"
                value={seoSettings.canonical_url}
                onChange={(e) => setSeoSettings({ ...seoSettings, canonical_url: e.target.value })}
                placeholder="https://smrtpdftools.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Preferred version of your site URL for search engines
              </p>
            </div>

            {/* Robots Meta */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Robots Meta Tag
              </label>
              <select
                value={seoSettings.robots_meta}
                onChange={(e) => setSeoSettings({ ...seoSettings, robots_meta: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="index, follow">index, follow (Allow search engines)</option>
                <option value="noindex, nofollow">noindex, nofollow (Block search engines)</option>
                <option value="index, nofollow">index, nofollow (Index but don't follow links)</option>
                <option value="noindex, follow">noindex, follow (Don't index but follow links)</option>
              </select>
            </div>

            {/* Enable Sitemap */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={seoSettings.enable_sitemap}
                  onChange={(e) => setSeoSettings({ ...seoSettings, enable_sitemap: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#2d3748'
                }}>
                  Enable sitemap.xml
                </span>
              </label>
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px', marginLeft: '32px' }}>
                Generate dynamic sitemap at /sitemap.xml
              </p>
            </div>

            {/* Enable Robots.txt */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={seoSettings.enable_robots}
                  onChange={(e) => setSeoSettings({ ...seoSettings, enable_robots: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#2d3748'
                }}>
                  Enable robots.txt
                </span>
              </label>
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px', marginLeft: '32px' }}>
                Generate dynamic robots.txt at /robots.txt
              </p>
            </div>

            {/* Save Button */}
            <div style={{
              borderTop: '2px solid #e2e8f0',
              paddingTop: '20px',
              marginTop: '30px'
            }}>
              <button
                onClick={handleSaveSEO}
                disabled={saving}
                style={{
                  background: saving ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  width: '100%'
                }}
              >
                {saving ? '💾 Saving...' : '💾 Save SEO Settings'}
              </button>
            </div>

            {/* Info Box */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: 'linear-gradient(135deg, #ebf8ff 0%, #e0f2fe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: '#1e40af',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>ℹ️</span> SEO Information
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: '#1e40af',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}>
                <li>SEO changes take effect immediately on the homepage</li>
                <li>Sitemap available at: <strong>/sitemap.xml</strong></li>
                <li>Robots.txt available at: <strong>/robots.txt</strong></li>
                <li>Submit your sitemap to Google Search Console for better indexing</li>
                <li>Meta tags are injected dynamically into the page head</li>
              </ul>
            </div>
          </div>
        )}

        {/* Analytics & Tracking Tab */}
        {activeTab === 'analytics' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              📊 Analytics & Tracking
            </h2>

            {/* Google Analytics ID */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Google Analytics Measurement ID
              </label>
              <input
                type="text"
                value={analyticsSettings.google_analytics_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: G-XXXXXXXXXX (Google Analytics 4)
              </p>
            </div>

            {/* Google Tag Manager ID */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Google Tag Manager ID
              </label>
              <input
                type="text"
                value={analyticsSettings.google_tag_manager_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, google_tag_manager_id: e.target.value })}
                placeholder="GTM-XXXXXX"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: GTM-XXXXXX (Google Tag Manager)
              </p>
            </div>

            {/* Facebook Pixel ID */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Facebook Pixel ID
              </label>
              <input
                type="text"
                value={analyticsSettings.facebook_pixel_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, facebook_pixel_id: e.target.value })}
                placeholder="123456789012345"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: 123456789012345 (Meta/Facebook Pixel)
              </p>
            </div>

            {/* LinkedIn Insight Tag ID */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                LinkedIn Insight Tag ID
              </label>
              <input
                type="text"
                value={analyticsSettings.linkedin_insight_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, linkedin_insight_id: e.target.value })}
                placeholder="1234567"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: 1234567 (LinkedIn Insight Tag)
              </p>
            </div>

            {/* Microsoft Ads UET Tag */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Microsoft Ads Tag (UET Tag)
              </label>
              <input
                type="text"
                value={analyticsSettings.microsoft_ads_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, microsoft_ads_id: e.target.value })}
                placeholder="12345678"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: 12345678 (Microsoft Advertising UET Tag)
              </p>
            </div>

            {/* TikTok Pixel ID */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                TikTok Pixel ID
              </label>
              <input
                type="text"
                value={analyticsSettings.tiktok_pixel_id}
                onChange={(e) => setAnalyticsSettings({ ...analyticsSettings, tiktok_pixel_id: e.target.value })}
                placeholder="ABCDEFG1234567890"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ color: '#718096', fontSize: '0.875rem', marginTop: '8px' }}>
                Example: ABCDEFG1234567890 (TikTok Pixel)
              </p>
            </div>

            {/* Save Button */}
            <div style={{
              borderTop: '2px solid #e2e8f0',
              paddingTop: '20px',
              marginTop: '30px'
            }}>
              <button
                onClick={handleSaveAnalytics}
                disabled={saving}
                style={{
                  background: saving ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  width: '100%'
                }}
              >
                {saving ? '💾 Saving...' : '💾 Save Analytics Settings'}
              </button>
            </div>

            {/* Info Box */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: 'linear-gradient(135deg, #ebf8ff 0%, #e0f2fe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: '#1e40af',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>ℹ️</span> Tracking Information
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: '#1e40af',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}>
                <li>Analytics scripts are injected automatically on every page</li>
                <li>Changes take effect immediately without requiring deployment</li>
                <li>Leave fields empty if you don't use a particular platform</li>
                <li>Only active tracking codes (non-empty) will be loaded</li>
                <li>All tracking codes are GDPR-compliant when used correctly</li>
                <li>Test your tracking with browser dev tools or respective platform debuggers</li>
              </ul>
            </div>
          </div>
        )}

        {/* Basic Content Settings Tab */}
        {activeTab === 'content' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              ✏️ Basic Content Settings
            </h2>

            {/* HOMEPAGE SECTION */}
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2d3748', marginTop: '20px', marginBottom: '15px' }}>
              🏠 Homepage
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Homepage Title
              </label>
              <input
                type="text"
                value={contentSettings.homepage_title}
                onChange={(e) => setContentSettings({ ...contentSettings, homepage_title: e.target.value })}
                placeholder="SmartPDF Tools"
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Homepage Subtitle
              </label>
              <textarea
                value={contentSettings.homepage_subtitle}
                onChange={(e) => setContentSettings({ ...contentSettings, homepage_subtitle: e.target.value })}
                placeholder="A fast, clean, ad-free alternative to iLovePDF & SmallPDF for only $5/month."
                rows={2}
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px', resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Homepage CTA Button Text
              </label>
              <input
                type="text"
                value={contentSettings.homepage_cta_text}
                onChange={(e) => setContentSettings({ ...contentSettings, homepage_cta_text: e.target.value })}
                placeholder="Get Started"
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            {/* HEADER PROMO SECTION */}
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2d3748', marginTop: '30px', marginBottom: '15px' }}>
              📢 Header Promo Bar
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  checked={contentSettings.header_promo_enabled}
                  onChange={(e) => setContentSettings({ ...contentSettings, header_promo_enabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#2d3748' }}>
                  Enable Header Promo Bar
                </span>
              </label>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Header Promo Text
              </label>
              <input
                type="text"
                value={contentSettings.header_promo_text}
                onChange={(e) => setContentSettings({ ...contentSettings, header_promo_text: e.target.value })}
                placeholder="A fast, clean, ad-free alternative to iLovePDF & SmallPDF."
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            {/* FOOTER SECTION */}
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2d3748', marginTop: '30px', marginBottom: '15px' }}>
              🔻 Footer
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Footer Promo Text
              </label>
              <input
                type="text"
                value={contentSettings.footer_promo_text}
                onChange={(e) => setContentSettings({ ...contentSettings, footer_promo_text: e.target.value })}
                placeholder="A fast, clean, ad-free PDF tool for everyday use."
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Footer Copyright Text
              </label>
              <input
                type="text"
                value={contentSettings.footer_copyright}
                onChange={(e) => setContentSettings({ ...contentSettings, footer_copyright: e.target.value })}
                placeholder="© 2025 SmartPDF Tools. All rights reserved."
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            {/* PRICING SECTION */}
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2d3748', marginTop: '30px', marginBottom: '15px' }}>
              💰 Pricing Page
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Pricing Title
              </label>
              <input
                type="text"
                value={contentSettings.pricing_title}
                onChange={(e) => setContentSettings({ ...contentSettings, pricing_title: e.target.value })}
                placeholder="Simple, Affordable Pricing"
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Pricing Subtitle
              </label>
              <input
                type="text"
                value={contentSettings.pricing_subtitle}
                onChange={(e) => setContentSettings({ ...contentSettings, pricing_subtitle: e.target.value })}
                placeholder="Only $5/month for unlimited ad-free PDF tools."
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            {/* META/SEO SECTION */}
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2d3748', marginTop: '30px', marginBottom: '15px' }}>
              🔍 Meta / SEO
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Meta Title
              </label>
              <input
                type="text"
                value={contentSettings.meta_title}
                onChange={(e) => setContentSettings({ ...contentSettings, meta_title: e.target.value })}
                placeholder="SmartPDF Tools – Fast, Clean, Ad-Free PDF Tools"
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Meta Description
              </label>
              <textarea
                value={contentSettings.meta_description}
                onChange={(e) => setContentSettings({ ...contentSettings, meta_description: e.target.value })}
                placeholder="A faster, cleaner alternative to iLovePDF & SmallPDF. Merge, compress, convert PDFs without ads or limits for only $5/month."
                rows={3}
                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', border: '2px solid #e2e8f0', borderRadius: '8px', resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {/* Save Button */}
            <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '30px' }}>
              <button
                onClick={handleSaveContent}
                disabled={saving}
                style={{
                  background: saving ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  width: '100%'
                }}
              >
                {saving ? '💾 Saving...' : '💾 Save Content Settings'}
              </button>
            </div>

            {/* Info Box */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: 'linear-gradient(135deg, #ebf8ff 0%, #e0f2fe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: '#1e40af',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>ℹ️</span> Content Information
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: '#1e40af',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}>
                <li>Content changes apply instantly without requiring deployment</li>
                <li>Leave fields empty to use default values</li>
                <li>Header promo bar can be toggled on/off anytime</li>
                <li>Changes reflect immediately on homepage, footer, and pricing page</li>
                <li>Meta title and description improve your SEO visibility</li>
              </ul>
            </div>
          </div>
        )}


        {/* Launch Offer Tab */}
        {activeTab === 'launch-offer' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              🚀 Launch Offer Settings
            </h2>

            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '30px'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#78350f', marginBottom: '8px', fontWeight: '600' }}>
                ⚡ How it works:
              </p>
              <ul style={{ fontSize: '0.85rem', color: '#78350f', paddingLeft: '20px', margin: 0 }}>
                <li>When enabled, all new sign-ups automatically get 1 month of Premium free</li>
                <li>The offer automatically expires on the date you set below</li>
                <li>The banner on the homepage will display your custom offer text</li>
                <li>After expiry, the banner hides automatically and new users register normally</li>
              </ul>
            </div>

            {/* Enable/Disable Toggle */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ position: 'relative', width: '50px', height: '28px' }}>
                  <input
                    type="checkbox"
                    checked={launchOfferSettings.launch_offer_enabled}
                    onChange={(e) => setLaunchOfferSettings({ ...launchOfferSettings, launch_offer_enabled: e.target.checked })}
                    style={{ display: 'none' }}
                    id="launch-offer-toggle"
                  />
                  <div 
                    onClick={() => setLaunchOfferSettings({ ...launchOfferSettings, launch_offer_enabled: !launchOfferSettings.launch_offer_enabled })}
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: launchOfferSettings.launch_offer_enabled ? '#22c55e' : '#cbd5e0',
                      borderRadius: '14px',
                      transition: 'background-color 0.3s',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: launchOfferSettings.launch_offer_enabled ? 'calc(100% - 26px)' : '2px',
                      width: '24px',
                      height: '24px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                </div>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: '#2d3748' }}>
                  {launchOfferSettings.launch_offer_enabled ? '✅ Launch Offer Enabled' : '⏸️ Launch Offer Disabled'}
                </span>
              </label>
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '8px', marginLeft: '62px' }}>
                {launchOfferSettings.launch_offer_enabled ? 'New users will automatically get 1 month free Premium' : 'Launch offer is paused. New users will register with Free plan'}
              </p>
            </div>

            {/* Banner Text */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                🎉 Banner Text
              </label>
              <textarea
                value={launchOfferSettings.launch_offer_text}
                onChange={(e) => setLaunchOfferSettings({ ...launchOfferSettings, launch_offer_text: e.target.value })}
                placeholder="🎉 Enjoy 1 month of Premium FREE with all new sign-ups. Offer ends Jan 15, 2026."
                rows={2}
                style={{ 
                  width: '100%', 
                  padding: '12px 14px', 
                  fontSize: '0.95rem', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '8px', 
                  resize: 'vertical', 
                  fontFamily: 'inherit' 
                }}
              />
              <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '6px' }}>
                This text will be displayed in the homepage banner
              </p>
            </div>

            {/* Expiry Date */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                ⏰ Offer Expiry Date & Time (UTC)
              </label>
              <input
                type="datetime-local"
                value={launchOfferSettings.launch_offer_expiry ? launchOfferSettings.launch_offer_expiry.slice(0, 16) : ''}
                onChange={(e) => {
                  const localDateTime = e.target.value;
                  const utcDateTime = localDateTime ? `${localDateTime}:59Z` : '';
                  setLaunchOfferSettings({ ...launchOfferSettings, launch_offer_expiry: utcDateTime });
                }}
                style={{ 
                  width: '100%', 
                  padding: '12px 14px', 
                  fontSize: '0.95rem', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '8px' 
                }}
              />
              <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '6px' }}>
                The offer will automatically expire at this date and time. After expiry, the banner will hide and new sign-ups will be normal.
              </p>
            </div>

            {/* Current Status */}
            <div style={{
              background: launchOfferSettings.launch_offer_enabled ? '#d1fae5' : '#fee2e2',
              border: `1px solid ${launchOfferSettings.launch_offer_enabled ? '#10b981' : '#ef4444'}`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '30px'
            }}>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: launchOfferSettings.launch_offer_enabled ? '#065f46' : '#7f1d1d', marginBottom: '4px' }}>
                {launchOfferSettings.launch_offer_enabled ? '🟢 OFFER ACTIVE' : '🔴 OFFER INACTIVE'}
              </p>
              <p style={{ fontSize: '0.85rem', color: launchOfferSettings.launch_offer_enabled ? '#065f46' : '#7f1d1d' }}>
                {launchOfferSettings.launch_offer_enabled 
                  ? `New users signing up before ${new Date(launchOfferSettings.launch_offer_expiry).toLocaleString()} will receive 1 month of Premium for free.`
                  : 'The launch offer is currently disabled. Enable it above to start offering 1 month free Premium to new sign-ups.'}
              </p>
            </div>

            {/* Save Button */}
            <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '30px' }}>
              <button
                onClick={handleSaveLaunchOffer}
                disabled={saving}
                style={{
                  background: saving ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
                  transition: 'all 0.3s'
                }}
              >
                {saving ? '💾 Saving...' : '💾 Save Launch Offer Settings'}
              </button>
            </div>

            {/* Help Section */}
            <div style={{
              background: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '30px'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                💡 Tips
              </h3>
              <ul style={{
                paddingLeft: '20px',
                color: '#1e40af',
                fontSize: '0.9rem',
                lineHeight: '1.6'
              }}>
                <li>Changes take effect instantly - no deployment needed</li>
                <li>Users who signed up during the offer period keep their 1 month of Premium</li>
                <li>After the expiry date, the banner automatically hides</li>
                <li>You can disable the offer anytime using the toggle above</li>
                <li>The offer expiry date is in UTC timezone</li>
              </ul>
            </div>
          </div>
        )}


        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              👥 User Management
            </h2>

            {/* Stats Summary */}
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '30px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>{totalUsers}</p>
                    <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>Total Registered Users</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '5px' }}>
                      {users.filter(u => u.subscription_status === 'pro' || u.subscription_status === 'yearly' || u.subscription_status === 'lifetime').length}
                    </p>
                    <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>Premium Users</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '5px' }}>
                      {users.filter(u => u.subscription_status === 'free').length}
                    </p>
                    <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>Free Users</p>
                  </div>
                </div>
                <button
                  onClick={handleExportCSV}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                  }}
                >
                  📥 Export to CSV
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#4a5568' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#4a5568' }}>Subscription</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#4a5568' }}>Registered</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#4a5568' }}>Expiry</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#4a5568' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.email || `user-${index}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px', fontSize: '0.9rem', color: '#2d3748' }}>
                        {user.email}
                        {user.role === 'admin' && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#fbbf24', color: 'white', borderRadius: '4px', fontSize: '0.75rem' }}>ADMIN</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          background: user.subscription_status === 'free' ? '#e5e7eb' : user.subscription_status === 'lifetime' ? '#fbbf24' : '#10b981',
                          color: user.subscription_status === 'free' ? '#374151' : 'white'
                        }}>
                          {user.subscription_status?.toUpperCase() || 'FREE'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.85rem', color: '#6b7280' }}>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.85rem', color: '#6b7280' }}>
                        {user.subscription_expiry ? new Date(user.subscription_expiry).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                          style={{
                            padding: '6px 16px',
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
                          onMouseOut={(e) => e.target.style.background = '#2563eb'}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                <p>No users found</p>
              </div>
            )}
          </div>
        )}

        {/* Seasonal Settings Tab */}
        {activeTab === 'seasonal' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              ❄️ Seasonal Settings
            </h2>

            {/* Auto-disable Notice */}
            {seasonalSettings.auto_disabled && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#92400e', fontWeight: '600' }}>
                  ⚠️ Seasonal features have been automatically disabled (past Jan 2, 2026)
                </p>
              </div>
            )}

            {/* Holiday Banner Info */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <span style={{ fontSize: '2rem' }}>🎄</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700' }}>Holiday Season 2025</h3>
                  <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>Features auto-disable on Jan 2, 2026</p>
                </div>
              </div>
            </div>

            {/* Snow Animation Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px',
              background: '#f8fafc',
              borderRadius: '10px',
              marginBottom: '15px',
              border: '1px solid #e2e8f0'
            }}>
              <div>
                <label style={{ fontWeight: '600', color: '#1a202c', display: 'block', marginBottom: '4px' }}>
                  ❄️ Snowfall Animation
                </label>
                <p style={{ fontSize: '0.85rem', color: '#718096' }}>
                  Show falling snowflakes across the entire site
                </p>
              </div>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '56px',
                height: '28px',
                cursor: seasonalSettings.auto_disabled ? 'not-allowed' : 'pointer',
                opacity: seasonalSettings.auto_disabled ? 0.5 : 1
              }}>
                <input
                  type="checkbox"
                  checked={seasonalSettings.snow_enabled}
                  onChange={(e) => setSeasonalSettings({ ...seasonalSettings, snow_enabled: e.target.checked })}
                  disabled={seasonalSettings.auto_disabled}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  inset: 0,
                  background: seasonalSettings.snow_enabled ? '#2563eb' : '#cbd5e0',
                  borderRadius: '28px',
                  transition: 'all 0.3s'
                }}>
                  <span style={{
                    position: 'absolute',
                    width: '22px',
                    height: '22px',
                    background: 'white',
                    borderRadius: '50%',
                    top: '3px',
                    left: seasonalSettings.snow_enabled ? '31px' : '3px',
                    transition: 'all 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </span>
              </label>
            </div>

            {/* Holiday Popup Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px',
              background: '#f8fafc',
              borderRadius: '10px',
              marginBottom: '25px',
              border: '1px solid #e2e8f0'
            }}>
              <div>
                <label style={{ fontWeight: '600', color: '#1a202c', display: 'block', marginBottom: '4px' }}>
                  🎁 Holiday Welcome Popup
                </label>
                <p style={{ fontSize: '0.85rem', color: '#718096' }}>
                  Show festive greeting on first login (only once per user)
                </p>
              </div>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '56px',
                height: '28px',
                cursor: seasonalSettings.auto_disabled ? 'not-allowed' : 'pointer',
                opacity: seasonalSettings.auto_disabled ? 0.5 : 1
              }}>
                <input
                  type="checkbox"
                  checked={seasonalSettings.holiday_popup_enabled}
                  onChange={(e) => setSeasonalSettings({ ...seasonalSettings, holiday_popup_enabled: e.target.checked })}
                  disabled={seasonalSettings.auto_disabled}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  inset: 0,
                  background: seasonalSettings.holiday_popup_enabled ? '#2563eb' : '#cbd5e0',
                  borderRadius: '28px',
                  transition: 'all 0.3s'
                }}>
                  <span style={{
                    position: 'absolute',
                    width: '22px',
                    height: '22px',
                    background: 'white',
                    borderRadius: '50%',
                    top: '3px',
                    left: seasonalSettings.holiday_popup_enabled ? '31px' : '3px',
                    transition: 'all 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </span>
              </label>
            </div>

            {/* Info Box */}
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '25px'
            }}>
              <p style={{ color: '#1e40af', fontSize: '0.9rem', lineHeight: '1.6' }}>
                <strong>ℹ️ Note:</strong> These seasonal features will automatically turn off on 
                <strong> January 2, 2026 at midnight</strong> (local time). No deployment required - 
                the date check happens client-side.
              </p>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveSeasonalSettings}
              disabled={saving || seasonalSettings.auto_disabled}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: saving || seasonalSettings.auto_disabled 
                  ? '#9ca3af' 
                  : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: saving || seasonalSettings.auto_disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {saving ? '💫 Saving...' : '💾 Save Seasonal Settings'}
            </button>
          </div>
        )}

        {/* Tools Visibility Tab */}
        {activeTab === 'tools' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px'
            }}>
              🛠️ Tool Visibility Settings
            </h2>

            <p style={{ color: '#718096', marginBottom: '25px', fontSize: '0.95rem' }}>
              Control which PDF tools are visible to users on the homepage. 
              Disabled tools will be hidden from the tools grid.
            </p>

            {/* Tool Toggles Grid */}
            <div style={{ display: 'grid', gap: '15px' }}>
              {[
                { key: 'merge', name: 'Merge PDFs', icon: '📄', desc: 'Combine multiple PDFs into one file' },
                { key: 'split', name: 'Split PDF', icon: '✂️', desc: 'Extract specific pages from PDF' },
                { key: 'compress', name: 'Compress PDF', icon: '📦', desc: 'Reduce file size without quality loss' },
                { key: 'convert_to_jpg', name: 'PDF to JPG', icon: '🖼️', desc: 'Convert PDF pages to images' },
                { key: 'pdf_to_word', name: 'PDF to Word', icon: '📝', desc: 'Convert PDF to editable Word documents' },
                { key: 'word_to_pdf', name: 'Word to PDF', icon: '📄', desc: 'Convert Word documents to PDF' },
                { key: 'xfa_to_pdf', name: 'XFA to PDF', icon: '🔄', desc: 'Convert XFA forms to standard PDFs' },
                { key: 'image_to_pdf', name: 'Image to PDF', icon: '🖼️', desc: 'Convert images to PDF' },
                { key: 'pdf_to_ppt', name: 'PDF to PPT', icon: '📊', desc: 'Convert slide-format PDFs back to PowerPoint (.pptx)' }
              ].map(tool => (
                <div
                  key={tool.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '18px 20px',
                    background: toolVisibility[tool.key] ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '10px',
                    border: `1px solid ${toolVisibility[tool.key] ? '#86efac' : '#fecaca'}`,
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ fontSize: '1.5rem' }}>{tool.icon}</span>
                    <div>
                      <label style={{ fontWeight: '600', color: '#1a202c', display: 'block', marginBottom: '2px' }}>
                        {tool.name}
                      </label>
                      <p style={{ fontSize: '0.8rem', color: '#718096' }}>{tool.desc}</p>
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '56px',
                    height: '28px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={toolVisibility[tool.key]}
                      onChange={(e) => setToolVisibility({ ...toolVisibility, [tool.key]: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      inset: 0,
                      background: toolVisibility[tool.key] ? '#22c55e' : '#ef4444',
                      borderRadius: '28px',
                      transition: 'all 0.3s'
                    }}>
                      <span style={{
                        position: 'absolute',
                        width: '22px',
                        height: '22px',
                        background: 'white',
                        borderRadius: '50%',
                        top: '3px',
                        left: toolVisibility[tool.key] ? '31px' : '3px',
                        transition: 'all 0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              ))}
            </div>

            {/* Info Box */}
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '15px',
              marginTop: '25px',
              marginBottom: '25px'
            }}>
              <p style={{ color: '#1e40af', fontSize: '0.9rem', lineHeight: '1.6' }}>
                <strong>ℹ️ Note:</strong> Changes take effect immediately after saving. 
                Hidden tools will not be accessible to users until re-enabled.
              </p>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveToolVisibility}
              disabled={saving}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: saving 
                  ? '#9ca3af' 
                  : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {saving ? '💫 Saving...' : '💾 Save Tool Visibility'}
            </button>
          </div>
        )}

        {/* User Management Modal */}
        {showUserModal && selectedUser && (
          <>
            <div 
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 50
              }}
              onClick={() => setShowUserModal(false)}
            />
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              zIndex: 51,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '20px', color: '#1a202c' }}>
                Manage Subscription: {selectedUser.email}
              </h3>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#4a5568' }}>
                  Current Subscription
                </label>
                <p style={{ fontSize: '1rem', fontWeight: '600', color: '#2563eb', marginBottom: '4px' }}>
                  {selectedUser.subscription_status?.toUpperCase() || 'FREE'}
                </p>
                {selectedUser.subscription_expiry && (
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Expires: {new Date(selectedUser.subscription_expiry).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#4a5568' }}>
                  Update Subscription To
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <button
                    onClick={() => handleUpdateSubscription(selectedUser.email, 'free', null)}
                    disabled={saving}
                    style={{
                      padding: '12px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s'
                    }}
                  >
                    Free
                  </button>
                  <button
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setMonth(expiry.getMonth() + 1);
                      handleUpdateSubscription(selectedUser.email, 'pro', expiry.toISOString());
                    }}
                    disabled={saving}
                    style={{
                      padding: '12px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s'
                    }}
                  >
                    Pro (1 Month)
                  </button>
                  <button
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setFullYear(expiry.getFullYear() + 1);
                      handleUpdateSubscription(selectedUser.email, 'yearly', expiry.toISOString());
                    }}
                    disabled={saving}
                    style={{
                      padding: '12px',
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s'
                    }}
                  >
                    Yearly (1 Year)
                  </button>
                  <button
                    onClick={() => handleUpdateSubscription(selectedUser.email, 'lifetime', null)}
                    disabled={saving}
                    style={{
                      padding: '12px',
                      background: '#fbbf24',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s'
                    }}
                  >
                    Lifetime
                  </button>
                </div>
              </div>

              {/* Delete User Section */}
              {selectedUser.role !== 'admin' && (
                <div style={{ 
                  marginTop: '20px', 
                  paddingTop: '20px', 
                  borderTop: '1px solid #fee2e2'
                }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: '#dc2626' 
                  }}>
                    ⚠️ Danger Zone
                  </label>
                  <button
                    onClick={() => handleDeleteUser(selectedUser.email)}
                    disabled={saving}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s',
                      opacity: saving ? 0.6 : 1
                    }}
                    onMouseOver={(e) => !saving && (e.target.style.background = '#b91c1c')}
                    onMouseOut={(e) => e.target.style.background = '#dc2626'}
                  >
                    🗑️ Delete User Permanently
                  </button>
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: '#9ca3af', 
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    This will permanently delete the user and all their data.
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUser(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Close
              </button>
            </div>
          </>
        )}


      </div>
    </div>
  );
};

export default AdminSettings;
